import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { vipAPI } from '../../../services/api';
import { useBrand } from '../../../context/BrandContext';
import { useAgent } from '../../../context/AgentContext';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../ui/table';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../ui/select';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { Spinner } from '../../ui/spinner';
import { useToast } from '../../ui/toast';
import { TablePagination, SortableHead } from '../../game_department/TableControls';
import { formatCents, STATUS_OPTIONS, STATUS_BADGE, todayISO, nextSort } from '../anniversary/format';
import { PlayerSearch } from '../anniversary/ModerationEditor';
import { usePlayerParam } from '../anniversary/playerUrl';
import ChristmasBonusRow, { COLUMN_COUNT } from './ChristmasBonusRow';
import { christmasParams, christmasYearOptions, christmasCountdown, UPCOMING, ALL_YEARS } from './format';

// Radix Select cannot hold an empty value, so "all" stands for "no filter".
const ALL = 'all';
const PAGE_SIZE = 100;

const initialFilters = () => ({ christmas: UPCOMING, status: ALL, bonusType: ALL, vipAgent: ALL, sortCol: '', sortDir: '', page: 1, pageSize: PAGE_SIZE });

const emptyData = { items: [], totals: [], total: 0 };

function FilterSelect({ label, value, onChange, options, includeAll = true, width = 'w-36' }) {
  return (
    <label className="flex flex-col gap-1 text-xs font-medium text-ink-dim">
      {label}
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className={`h-9 ${width} text-sm`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {includeAll && <SelectItem value={ALL}>All</SelectItem>}
          {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </label>
  );
}

// Summary shows totals over every row matching the filters (from the API), not only this page.
function Summary({ totals, total }) {
  // "To issue" leaves out bonuses already paid out; scheduled ones are booked but not yet issued.
  const amount = totals.reduce((s, t) => (t.status === 'issued' ? s : s + (t.effective_amount_usd_cents || 0)), 0);
  return (
    <div className="flex flex-wrap gap-2 text-xs font-medium">
      <span className="rounded-md bg-surface-2 px-2.5 py-1 text-ink">
        {total} proposals · <span className="font-bold">{formatCents(amount)}</span> to issue
      </span>
      {totals.map((t) => (
        <span key={t.status || 'unknown'} className="rounded-md bg-surface-2 px-2.5 py-1 text-ink-dim">
          {STATUS_BADGE[t.status]?.label || t.status || 'unknown'}: {t.count} · {formatCents(t.effective_amount_usd_cents)}
        </span>
      ))}
    </div>
  );
}

export default function ChristmasBonusesPage() {
  const { activeBrand } = useBrand();
  const { agents } = useAgent();
  const { show, ToastComponent } = useToast();
  const [filters, setFilters] = useState(initialFilters);
  const [player, setPlayerParam] = usePlayerParam();
  const [data, setData] = useState(emptyData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const params = useMemo(() => christmasParams({ ...filters, player }, todayISO()), [filters, player]);
  const yearOptions = useMemo(() => christmasYearOptions(), []);

  // Only the latest request may update state, so a slow response for earlier filters, pages,
  // sorts or brand cannot overwrite the current one.
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const isLatest = () => requestId === requestIdRef.current;
    setLoading(true);
    setError('');
    try {
      const res = await vipAPI.getChristmasBonuses(params);
      if (isLatest()) {
        setData({ items: res.data.items || [], totals: res.data.totals || [], total: res.data.total || 0 });
      }
    } catch (e) {
      if (isLatest()) setError(e?.response?.data?.error || 'Failed to load Christmas bonuses.');
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load, activeBrand]);

  // Any filter change goes back to the first page.
  const setFilter = (key) => (value) => setFilters((f) => ({ ...f, [key]: value, page: 1 }));
  const setPlayer = useCallback((text) => {
    setPlayerParam(text);
    setFilters((f) => ({ ...f, page: 1 }));
  }, [setPlayerParam]);
  const setPage = (page) => setFilters((f) => ({ ...f, page }));
  // A new order starts again from the first page.
  const onSort = (col) => setFilters((f) => ({ ...f, ...nextSort(f, col), page: 1 }));
  const sortProps = { sortCol: filters.sortCol, sortDir: filters.sortDir, onSort };

  const moderate = async (row, body) => {
    setBusyId(row.moderation_id);
    try {
      const res = await vipAPI.moderateChristmasBonus(row.moderation_id, body);
      setData((d) => ({ ...d, items: d.items.map((r) => (r.moderation_id === row.moderation_id ? res.data : r)) }));
      setEditingId(null);
      show(`Bonus for player ${row.user_id} ${res.data.status}.`, 'success');
      load(); // refresh the totals
    } catch (e) {
      show(e?.response?.data?.error || 'Saving failed.', 'error');
      if (e?.response?.status === 409) {
        setEditingId(null);
        load();
      }
    } finally {
      setBusyId(null);
    }
  };

  // Every proposal of one Christmas shares the date, so it is shown once here.
  const first = data.items[0];
  const totalPages = Math.max(1, Math.ceil(data.total / filters.pageSize));

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-bold text-ink">Christmas Bonuses</h1>
        <p className="text-xs font-medium text-ink-dim">
          Christmas bonuses are issued to all players on the same day. Pending and approved bonuses are issued at the proposed amount.
          Changes close 1 day before Christmas.
        </p>
        {first?.next_christmas && filters.christmas !== ALL_YEARS && (
          <p className="mt-1 text-sm font-bold text-ink">
            Christmas {first.next_christmas}
            <span className="ml-2 text-xs font-medium text-ink-dim">{christmasCountdown(first.days_until_christmas)}</span>
          </p>
        )}
      </div>

      <Card className="flex flex-wrap items-end gap-3 p-3">
        <FilterSelect label="Christmas" value={filters.christmas} onChange={setFilter('christmas')} includeAll={false}
          options={[[UPCOMING, 'Upcoming'], [ALL_YEARS, 'All years'], ...yearOptions.map((y) => [y, y])]} />
        <FilterSelect label="Status" value={filters.status} onChange={setFilter('status')}
          options={STATUS_OPTIONS.map((s) => [s, STATUS_BADGE[s].label])} />
        <FilterSelect label="Player type" value={filters.bonusType} onChange={setFilter('bonusType')}
          options={[['vip', 'VIP'], ['regular', 'Regular']]} />
        <FilterSelect label="VIP manager" value={filters.vipAgent} onChange={setFilter('vipAgent')} width="w-44"
          options={agents.map((a) => [a, a])} />
        <PlayerSearch value={player} onChange={setPlayer} />
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => { setFilters(initialFilters()); setPlayerParam(''); }}>Reset</Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
      </Card>

      {!loading && !error && <Summary totals={data.totals} total={data.total} />}
      {error && <p className="text-xs font-medium text-negative">{error}</p>}

      <Card className="overflow-x-auto p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Player</TableHead>
              <SortableHead col="level" label="Level" {...sortProps} />
              <TableHead>Type</TableHead>
              <SortableHead col="date" label="Christmas" {...sortProps} />
              <SortableHead col="ngr" label="NGR 1y" right {...sortProps} />
              <SortableHead col="proposed" label="Proposed" right {...sortProps} />
              <SortableHead col="override" label="Override" right {...sortProps} />
              <TableHead>Status</TableHead>
              <TableHead>Moderated</TableHead>
              <TableHead>Note</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={COLUMN_COUNT} className="py-10 text-center"><Spinner /></TableCell></TableRow>
            ) : data.items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="py-10 text-center text-sm font-medium text-ink-dim">
                  No Christmas bonuses match these filters.
                </TableCell>
              </TableRow>
            ) : (
              data.items.map((row) => (
                <ChristmasBonusRow
                  key={row.moderation_id}
                  row={row}
                  editing={editingId === row.moderation_id}
                  busy={busyId === row.moderation_id}
                  onEdit={() => setEditingId(row.moderation_id)}
                  onClose={() => setEditingId(null)}
                  onModerate={moderate}
                />
              ))
            )}
          </TableBody>
        </Table>
      </Card>
      {!loading && (
        <TablePagination page={filters.page} totalPages={totalPages} total={data.total} onPageChange={setPage} noun="proposal" />
      )}
      {ToastComponent}
    </div>
  );
}
